/**
 * Calculs dérivés COF2 - PV, DEF, Initiative, PM, PC, DR, valeurs d'attaque.
 *
 * Fonctions pures : reçoivent tout le contexte en argument, retournent des
 * résultats déterministes. Aucune dépendance au state global.
 *
 * Formules (regles.json -> creation_personnage.formules) :
 * - PV départ : 2 x PV_famille + CON  (+ bonus capacités)
 * - DEF : 10 + AGI + mod_armure + mod_bouclier + cap + magie (cap total <= +8, ou <= +10 avec grand bouclier)
 * - Initiative : 10 + PER + bonus_capacités
 * - PC : 2 + CHA (+ bonus famille aventurier)
 * - PM : VOL + nb_sorts_connus (0 si aucun sort)
 * - DR : 2 + CON (+1 mystique) ; type selon famille
 * - Att.C : niveau + FOR + bonus
 * - Att.D : niveau + AGI + bonus
 * - Att.M : niveau + VOL (ou INT/CHA/PER selon profil, à gérer en aval)
 */

import type { Carac, Caracs } from './caracs';
import type { FamilleId } from './famille';
import { FAMILLES, drCount, pcBase, pvAtLevel1 } from './famille';

/** Cap COF2 sur le bonus total de DEF hors 10 de base + carac. */
export const DEF_CAP_BASE = 8;
/** Cap si un grand bouclier est équipé (bonus_def >= 2 en convention interne). */
export const DEF_CAP_LARGE_SHIELD = 10;

/**
 * Stats dérivées retournées par `calcDerived`.
 */
export interface DerivedStats {
  readonly pv: number;
  readonly pvDetail: {
    readonly base: number;
    readonly capBonus: number;
  };

  readonly def: number;
  readonly defDetail: {
    readonly base: number; // toujours 10
    readonly caracValue: number; // AGI ou CON si swap
    readonly caracName: 'AGI' | 'CON';
    readonly armure: number;
    readonly bouclier: number;
    readonly capBonus: number;
    readonly magieBonus: number;
    readonly capApplique: boolean; // true si le cap a écrêté
  };

  readonly initiative: number;
  readonly initiativeDetail: {
    readonly base: number; // toujours 10
    readonly perValue: number;
    readonly capBonus: number;
  };

  readonly pc: number;
  readonly pm: number | null; // null si pas lanceur de sorts
  readonly drCount: number;
  readonly drType: 'd6' | 'd8' | 'd10';

  readonly attContact: number;
  readonly attDistance: number;
  readonly attMagique: number;
}

export interface DerivedInput {
  readonly niveau: number;
  readonly famille: FamilleId;
  readonly caracs: Caracs;
  readonly nbSortsConnus: number;
  /** Si true, utilise CON à la place d'AGI pour la DEF (ex: Peau de pierre). */
  readonly useConForDef?: boolean;
  /** Bonus provenant des capacités (cf. `effets.resolveEffects`). */
  readonly capBonuses?: {
    readonly pv?: number;
    readonly def?: number;
    readonly init?: number;
    readonly attContact?: number;
    readonly attDistance?: number;
    readonly attMagique?: number;
    readonly pm?: number;
  };
  /** Cap AGI de l'armure portée (null/undefined = pas de cap). */
  readonly agiMax?: number | null;
  /** Bonus armure et bouclier issus de l'équipement. */
  readonly armureBonus?: number;
  readonly bouclierBonus?: number;
  /** Bouclier large (grand) -> cap passe à +10. */
  readonly grandBouclier?: boolean;
  /** Bonus magique appliqué à la DEF (amulettes etc.) - non soumis au cap. */
  readonly magieBonus?: number;
}

/**
 * Calcule toutes les stats dérivées depuis l'input.
 */
export function calcDerived(input: DerivedInput): DerivedStats {
  const defDetail = calcDEFDetail(input);
  return {
    pv: calcPV(input),
    pvDetail: {
      base: pvAtLevel1(input.famille, input.caracs.CON),
      capBonus: input.capBonuses?.pv ?? 0,
    },
    def:
      defDetail.base +
      defDetail.caracValue +
      defDetail.armure +
      defDetail.bouclier +
      defDetail.capBonus +
      defDetail.magieBonus,
    defDetail,
    initiative: calcInit(input),
    initiativeDetail: {
      base: 10,
      perValue: input.caracs.PER,
      capBonus: input.capBonuses?.init ?? 0,
    },
    pc: pcBase(input.famille, input.caracs.CHA),
    pm: calcPM(input),
    drCount: drCount(input.famille, input.caracs.CON),
    drType: FAMILLES[input.famille].dr,
    attContact: calcAtt(input, 'contact'),
    attDistance: calcAtt(input, 'distance'),
    attMagique: calcAtt(input, 'magique'),
  };
}

/**
 * PV au niveau N.
 *
 * Formule : PV(1) = 2 x PV_famille + CON
 *            PV(N) = PV(1) + Σ (PV_famille + CON)  pour chaque niveau >= 2
 *         = PV_famille x (N+1) + CON x N (convient pour mono-profil)
 * + bonus capacités.
 */
export function calcPV(input: DerivedInput): number {
  const fam = FAMILLES[input.famille];
  const { niveau, caracs } = input;
  const base = fam.pvPerLevel * (niveau + 1) + caracs.CON * niveau;
  const bonus = input.capBonuses?.pv ?? 0;
  return Math.max(1, base + bonus);
}

export function calcInit(input: DerivedInput): number {
  return 10 + input.caracs.PER + (input.capBonuses?.init ?? 0);
}

export function calcDEFValue(input: DerivedInput): number {
  const d = calcDEFDetail(input);
  return d.base + d.caracValue + d.armure + d.bouclier + d.capBonus + d.magieBonus;
}

function calcDEFDetail(input: DerivedInput): DerivedStats['defDetail'] {
  const useCon = input.useConForDef === true;
  const caracName: 'AGI' | 'CON' = useCon ? 'CON' : 'AGI';
  const rawCaracValue = input.caracs[caracName];
  const caracValue =
    !useCon && input.agiMax != null ? Math.min(rawCaracValue, input.agiMax) : rawCaracValue;
  const armure = input.armureBonus ?? 0;
  const bouclier = input.bouclierBonus ?? 0;
  const rawCapBonus = input.capBonuses?.def ?? 0;
  const magieBonus = input.magieBonus ?? 0;

  // Cap : somme armure + bouclier + capBonus (hors base 10, carac, magie) <= cap.
  const cap = input.grandBouclier === true ? DEF_CAP_LARGE_SHIELD : DEF_CAP_BASE;
  const rawTotal = armure + bouclier + rawCapBonus;
  let cappedCapBonus = rawCapBonus;
  let capApplique = false;
  if (rawTotal > cap) {
    const excess = rawTotal - cap;
    cappedCapBonus = Math.max(0, rawCapBonus - excess);
    capApplique = true;
  }

  return {
    base: 10,
    caracValue,
    caracName,
    armure,
    bouclier,
    capBonus: cappedCapBonus,
    magieBonus,
    capApplique,
  };
}

export function calcPM(input: DerivedInput): number | null {
  if (input.nbSortsConnus <= 0) return null;
  const bonus = input.capBonuses?.pm ?? 0;
  return Math.max(0, input.caracs.VOL + input.nbSortsConnus + bonus);
}

export function calcAtt(input: DerivedInput, mode: 'contact' | 'distance' | 'magique'): number {
  const { niveau, caracs } = input;
  const capBonus =
    mode === 'contact'
      ? (input.capBonuses?.attContact ?? 0)
      : mode === 'distance'
        ? (input.capBonuses?.attDistance ?? 0)
        : (input.capBonuses?.attMagique ?? 0);

  const carac: Carac = mode === 'contact' ? 'FOR' : mode === 'distance' ? 'AGI' : 'VOL';

  return Math.min(niveau, 10) + caracs[carac] + capBonus;
}
